import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { useTable, useSortBy, useExpanded, useGlobalFilter } from 'react-table';
import { Table } from 'react-bootstrap';
import { useForecast } from '../../providers/ForecastProvider';
import { useProjection } from '../../providers/ProjectionProvider';
import { calculateForecastQuantity, calculateForecastRevenue, calculateRevenueGrowth } from '../../utils/calculation';
import { hideLoading, showLoading } from '../../lib/uiService';
import TableHead from '../../components/common/TableHead';
import TableBody from '../../components/common/TableBody';
import { useBrandFamilyProjectionsTableColumns } from './BrandFamilyProjectionsTableHooks';
import { toast } from 'react-toastify';

const BrandFamilyProjectionsTable: React.FC<any> = ({ projection }: { projection: any }) => {
  const { id: projectionId } = useParams();

  const { forecastId, territoryId, hasForecasts, forecastCalendarId } = useForecast();
  const [brandForecasts, setBrandForecasts] = useState<any[]>([]);
  const [initialData, setInitialData] = useState<any[]>([]);
  const projectionContext = useProjection();
  const [isDirty, setIsDirty] = useState(false);
  const [isAutoSave, setIsAutoSave] = useState(false);

  useEffect(() => {
    const localStorageCalendarId = localStorage.getItem('timePeriod') || '';
    const calendarId = forecastCalendarId ? forecastCalendarId : localStorageCalendarId;
    if (hasForecasts && forecastId && calendarId && territoryId) {
      if (projection) {
        const changes = projection.getBrandFamilyChanges();
        const filteredBrandChanges = changes.filter((change: any) => {
          return (
            change.forecastBudget.territoryId === territoryId && change.forecastBudget.forecastCalendarId === calendarId
          );
        });
        setInitialData(filteredBrandChanges);
        setBrandForecasts(filteredBrandChanges);
      } else {
        setInitialData([]);
        setBrandForecasts([]);
      }
    }
  }, [forecastId, territoryId, forecastCalendarId, projection]);

  useEffect(() => {
    if (isAutoSave) handleSave();
  }, [brandForecasts]);

  const handleChange = (id: string, updatedData: any) => {
    setBrandForecasts((prevData) => {
      return prevData.map((row) => {
        if (row.forecastBudgetProjectionId === id) {
          const newRow = {
            ...row,
            ...updatedData,
          };
          const newForecastRevenue = calculateForecastRevenue(
            row.forecastBudget.revenue,
            row.forecastBudget.salesGrowth,
            row.forecastBudget.priceGrowth,
            newRow.salesGrowth,
            newRow.priceGrowth,
          );
          const newRevenueGrowth = calculateRevenueGrowth(newRow['salesGrowth'], newRow['priceGrowth']);
          if (Object.keys(updatedData).includes('salesGrowth')) {
            const newForecastQuantity = calculateForecastQuantity(
              row.forecastBudget.quantity,
              row.forecastBudget.salesGrowth,
              newRow.salesGrowth,
            );
            return {
              ...newRow,
              quantity: newForecastQuantity,
              revenue: newForecastRevenue,
              revenueGrowth: newRevenueGrowth,
            };
          } else {
            return {
              ...newRow,
              revenue: newForecastRevenue,
            };
          }
        } else {
          return row;
        }
      });
    });
    setIsDirty(true);
  };

  const handleRemove = (id: string) => {
    setBrandForecasts((prevData) => {
      return prevData.filter((row) => row.forecastBudgetProjectionId !== id);
    });
    setIsAutoSave(true);
  };

  const handleSave = () => {
    showLoading();
    const changedRows = brandForecasts.filter((brandForecast, index) => {
      return (
        brandForecast.salesGrowth !== initialData[index].salesGrowth ||
        brandForecast.priceGrowth !== initialData[index].priceGrowth
      );
    });
    if (changedRows.length) {
      const data = changedRows.map(({ forecastBudgetId, salesGrowth, priceGrowth }) => ({
        forecastBudgetId,
        salesGrowth,
        priceGrowth,
      }));

      projectionContext.handlers
        .update({
          territoryId,
          projectionId,
          isExec: false,
          data,
        })
        .then(() => {
          hideLoading();
          setInitialData(brandForecasts);
          toast.success('Successfully Updated Brand Family Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
    }
    // find removed rows
    const removedRows = initialData.filter((initialRow) => {
      return !brandForecasts.find((row) => row.forecastBudgetProjectionId === initialRow.forecastBudgetProjectionId);
    });
    if (removedRows.length) {
      const data = removedRows.map(({ forecastBudgetProjectionId }) => ({
        forecastBudgetProjectionId,
      }));
      projectionContext.handlers
        .discardLine({
          territoryId,
          projectionId,
          data,
        })
        .then(() => {
          projectionContext.handlers.fetch(forecastId);
          hideLoading();
          setInitialData(brandForecasts);
          toast.success('Successfully discarded Brand Family Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
    }
    setIsDirty(false);
    setIsAutoSave(false);
  };

  const handleDiscardAll = () => {
    brandForecasts.forEach((row) => {
      handleRemove(row.forecastBudgetProjectionId);
    });
    setIsAutoSave(true);
  };

  const handleSaveItem = React.useCallback(
    (changedData?: any) => {
      const changedRows = changedData;

      if (changedRows.length === 0) return;
      const data = changedRows.map(({ forecastBudgetId, salesGrowth, priceGrowth, explanation, quantity }: any) => ({
        forecastBudgetId,
        salesGrowth,
        priceGrowth,
        explanation,
        quantity,
      }));
      showLoading();
      projectionContext.handlers
        .update({
          territoryId,
          forecastId,
          isExec: false,
          data,
        })
        .then(() => {
          hideLoading();
          setInitialData(brandForecasts);
          toast.success('Successfully Updated Brand Family Projection');
        })
        .catch((error) => {
          hideLoading();
          console.log(error);
        });
      hideLoading();
    },
    [brandForecasts],
  );

  const columns = useBrandFamilyProjectionsTableColumns({ handleChange, handleRemove, handleSaveItem });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: useMemo(() => brandForecasts, [brandForecasts]),
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
  );

  return (
    <div className="card shadow" style={{ marginBottom: '44px' }}>
      <div className="card-header py-3">
        <p className="text-primary m-0 fw-bold">Brand Family Projections</p>
      </div>
      <div className="card-body">
        <div>
          <Table striped className="table-sm" bordered {...getTableProps()} responsive>
            <TableHead headerGroups={headerGroups} />
            <TableBody
              {...getTableBodyProps()}
              rows={rows}
              renderRow={(row: any, key: number) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={key} className={`table-body-font-size`}>
                    {row.cells.map((cell: any, index: number) => {
                      return (
                        <td
                          {...cell.getCellProps()}
                          key={`${key}-${index}`}
                          style={{
                            border: 'none',
                            backgroundColor: '#fff',
                          }}
                          className={'text-dark align-middle'}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              }}
            />
            <tfoot>
              <tr>
                {columns.map((column: any, index: number) => {
                  return (
                    <td key={index} className="cursor-pointer">
                      {column?.Footer?.({ data: brandForecasts })}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </Table>
        </div>
        <div className="row">
          <div className="col-6">
            <button
              disabled={brandForecasts.length === 0}
              className="btn btn-secondary float-start"
              type="button"
              onClick={handleDiscardAll}
            >
              Discard All Brand Family Projections
            </button>
          </div>
          <div className="col-6">
            <button
              className={cx('btn float-end', isDirty ? 'btn-primary' : 'btn-secondary')}
              type="button"
              onClick={handleSave}
              disabled={!isDirty}
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandFamilyProjectionsTable;
